/**
 * NexusBackground — Canvas 2D procedural background.
 *
 * A premium multi-layer scene rendered every frame:
 *   1. Volumetric glow blobs (large, blurred, slowly drifting).
 *   2. Rare particles floating upward with parallax.
 *   3. Circuit traces + moving light pulses ONLY around the edges
 *      (left, right, top, bottom rails) — center stays clean so
 *      content is fully readable.
 *   4. Very slow, very dim binary rain drifting on the sides.
 *   5. Procedural noise dither (per-pixel jitter) to kill flat gradients.
 *
 * Automatically down-shifts complexity on low-power devices via
 * `matchMedia('(prefers-reduced-motion: reduce)')` and by measuring
 * `hardwareConcurrency`.
 */
import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const ACCENT_HSL: Record<string, [number, number, number]> = {
  turquesa: [174, 78, 55],
  azul:     [211, 92, 60],
  verde:    [142, 76, 50],
  morado:   [265, 84, 68],
  rosa:     [335, 82, 62],
};

interface Particle { x: number; y: number; vy: number; r: number; a: number; }
interface Pulse    { edge: 'top' | 'right' | 'bottom' | 'left'; t: number; speed: number; }
interface Column   { x: number; y: number; speed: number; chars: string[]; }

export const NexusBackground: React.FC = () => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const { accent, neon, resolved } = useTheme();

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // ── Device-tier detection ────────────────────────────────────
    const lowPower =
      (navigator as any).hardwareConcurrency && (navigator as any).hardwareConcurrency <= 2;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Cap DPR at 1.5 for performance; low-power devices at 1.
    const dpr = Math.min(window.devicePixelRatio || 1, lowPower ? 1 : 1.5);

    const [ah, as, al] = ACCENT_HSL[accent] || ACCENT_HSL.turquesa;
    const isDark = resolved === 'dark';
    const bgFill = isDark ? '#050608' : '#f6f7fb';
    const neonBoost = neon ? 1 : 0.55;

    let width = 0, height = 0, mx = 0, my = 0;

    // ── State pools ──────────────────────────────────────────────
    const particles: Particle[] = [];
    const pulses: Pulse[] = [];
    const binaryCols: Column[] = [];

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width  = Math.max(rect.width,  window.innerWidth);
      height = Math.max(rect.height, window.innerHeight);
      canvas.width  = Math.floor(width  * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width  = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Seed particles proportionally to viewport.
      const targetParticles = lowPower ? 24 : 60;
      particles.length = 0;
      for (let i = 0; i < targetParticles; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vy: 0.15 + Math.random() * 0.35,
          r: 0.5 + Math.random() * 1.5,
          a: 0.15 + Math.random() * 0.35,
        });
      }
      // Binary rain columns only on left/right side strips.
      binaryCols.length = 0;
      const colCount = lowPower ? 6 : 14;
      for (let i = 0; i < colCount; i++) {
        const onLeft = i % 2 === 0;
        binaryCols.push({
          x: onLeft
            ? 4 + Math.random() * Math.min(80, width * 0.08)
            : width - (4 + Math.random() * Math.min(80, width * 0.08)),
          y: Math.random() * height,
          speed: 0.25 + Math.random() * 0.6,
          chars: Array.from({ length: 8 }, () => (Math.random() > 0.5 ? '1' : '0')),
        });
      }
    };

    const onMouse = (e: MouseEvent) => {
      mx = e.clientX; my = e.clientY;
    };

    // Spawn one pulse every ~1.8s (each edge).
    const spawnPulse = () => {
      const edges: Pulse['edge'][] = ['top', 'right', 'bottom', 'left'];
      pulses.push({
        edge: edges[Math.floor(Math.random() * 4)],
        t: 0,
        speed: 0.0025 + Math.random() * 0.0025,
      });
      if (pulses.length > 8) pulses.shift();
    };

    resize();
    window.addEventListener('resize', resize);
    if (!reduceMotion) window.addEventListener('pointermove', onMouse, { passive: true });
    const pulseTimer = window.setInterval(spawnPulse, 1400);
    // Pre-seed a couple of pulses so the first frame isn't empty.
    for (let i = 0; i < 3; i++) spawnPulse();

    // ── Frame loop ───────────────────────────────────────────────
    let raf = 0;
    let last = performance.now();
    const draw = (now: number) => {
      raf = requestAnimationFrame(draw);
      const dt = Math.min(48, now - last); last = now;

      // Clear with base color
      ctx.fillStyle = bgFill;
      ctx.fillRect(0, 0, width, height);

      // ─ Layer 1 — volumetric glow blobs ─────────────────────────
      // Feb 2026 v3: removed the bottom-right blob (users reported it
      // showed as a "portal" behind the fixed nav). Kept a top-left
      // ambient tint and a very soft center wash.
      const t = now * 0.00006;
      const blobs = [
        { x: width * (0.15 + 0.05 * Math.sin(t * 1.3)),      y: height * (0.15 + 0.04 * Math.cos(t * 1.1)), r: Math.max(width, height) * 0.55, a: 0.14 },
        { x: width * 0.5,                                     y: height * 0.35,                              r: Math.max(width, height) * 0.5,  a: 0.06 },
      ];
      for (const b of blobs) {
        const grad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
        grad.addColorStop(0, `hsla(${ah}, ${as}%, ${al}%, ${b.a * neonBoost})`);
        grad.addColorStop(1, `hsla(${ah}, ${as}%, ${al}%, 0)`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      }

      // Parallax mouse tint (subtle)
      if (!reduceMotion && mx > 0) {
        const grad = ctx.createRadialGradient(mx, my, 0, mx, my, 380);
        grad.addColorStop(0, `hsla(${ah}, ${as}%, ${al}%, ${0.10 * neonBoost})`);
        grad.addColorStop(1, `hsla(${ah}, ${as}%, ${al}%, 0)`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      }

      // ─ Layer 2 — Edge circuits + light pulses ──────────────────
      // Only on the outer 60-90px band so the center stays clean.
      const band = Math.min(90, Math.max(48, width * 0.05));

      // ─ Layer 2.5 (Feb 2026 v4) — nearly-invisible circuit whisper ─
      // User feedback (17 Feb): "elimina la cuadrícula" — the previous
      // full-screen mesh was too dominant over uploaded backgrounds.
      // We keep just faint dots at rare grid intersections (~5% of the
      // previous density) so the "tech" feel doesn't vanish entirely
      // when the user hasn't set a custom background.
      const meshStep = 96;
      ctx.fillStyle = `hsla(${ah}, ${as}%, ${al + 8}%, ${0.09 * neonBoost})`;
      for (let gx = meshStep; gx < width - meshStep; gx += meshStep) {
        for (let gy = meshStep; gy < height - meshStep; gy += meshStep) {
          // Only ~1/9 of dots → very sparse.
          if (((gx / meshStep) * 13 + (gy / meshStep) * 7) % 9 !== 0) continue;
          ctx.beginPath();
          ctx.arc(gx, gy, 0.9, 0, Math.PI * 2); ctx.fill();
        }
      }

      ctx.strokeStyle = `hsla(${ah}, ${as}%, ${al}%, ${0.18 * neonBoost})`;
      ctx.lineWidth = 1;
      // Left rail
      const leftRail = 22;
      ctx.beginPath();
      ctx.moveTo(leftRail, band * 0.5);
      ctx.lineTo(leftRail, height - band * 0.5);
      ctx.stroke();
      // Right rail
      const rightRail = width - 22;
      ctx.beginPath();
      ctx.moveTo(rightRail, band * 0.5);
      ctx.lineTo(rightRail, height - band * 0.5);
      ctx.stroke();
      // Top rail
      ctx.beginPath();
      ctx.moveTo(band * 0.5, 22); ctx.lineTo(width - band * 0.5, 22); ctx.stroke();
      // Bottom rail
      ctx.beginPath();
      ctx.moveTo(band * 0.5, height - 22); ctx.lineTo(width - band * 0.5, height - 22); ctx.stroke();
      // Small ticks every 80px
      ctx.strokeStyle = `hsla(${ah}, ${as}%, ${al}%, ${0.10 * neonBoost})`;
      for (let y = 40; y < height - 40; y += 80) {
        ctx.beginPath(); ctx.moveTo(leftRail - 6, y); ctx.lineTo(leftRail + 6, y); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(rightRail - 6, y); ctx.lineTo(rightRail + 6, y); ctx.stroke();
      }
      for (let x = 40; x < width - 40; x += 80) {
        ctx.beginPath(); ctx.moveTo(x, 16); ctx.lineTo(x, 28); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x, height - 28); ctx.lineTo(x, height - 16); ctx.stroke();
      }

      // Pulses along the edges (moving light dots)
      for (const p of pulses) {
        p.t += p.speed * (dt / 16);
        if (p.t > 1) p.t = 0;
        let px = 0, py = 0;
        switch (p.edge) {
          case 'top':    px = 22 + p.t * (width - 44);  py = 22; break;
          case 'right':  px = rightRail;                 py = 22 + p.t * (height - 44); break;
          case 'bottom': px = width - 22 - p.t * (width - 44); py = height - 22; break;
          case 'left':   px = leftRail;                   py = height - 22 - p.t * (height - 44); break;
        }
        const grad = ctx.createRadialGradient(px, py, 0, px, py, 30);
        grad.addColorStop(0, `hsla(${ah}, ${as}%, ${al + 15}%, ${0.85 * neonBoost})`);
        grad.addColorStop(1, `hsla(${ah}, ${as}%, ${al}%, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath(); ctx.arc(px, py, 30, 0, Math.PI * 2); ctx.fill();
      }

      // ─ Layer 3 — Floating particles (parallax by radius) ───────
      for (const pt of particles) {
        pt.y -= pt.vy * (dt / 16);
        if (pt.y < -4) { pt.y = height + 4; pt.x = Math.random() * width; }
        ctx.fillStyle = `hsla(${ah}, ${as}%, ${al + 10}%, ${pt.a * neonBoost})`;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, pt.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // ─ Layer 4 — Very slow binary rain on side strips ─────────
      if (!reduceMotion) {
        ctx.font = '10px ui-monospace, monospace';
        ctx.fillStyle = `hsla(${ah}, ${as}%, ${al}%, ${0.12 * neonBoost})`;
        for (const c of binaryCols) {
          c.y += c.speed * (dt / 16);
          if (c.y > height + 100) { c.y = -80; c.chars = c.chars.map(() => (Math.random() > 0.5 ? '1' : '0')); }
          for (let i = 0; i < c.chars.length; i++) {
            const alpha = (0.14 - i * 0.014) * neonBoost;
            if (alpha <= 0) continue;
            ctx.fillStyle = `hsla(${ah}, ${as}%, ${al}%, ${alpha})`;
            ctx.fillText(c.chars[i], c.x, c.y - i * 12);
          }
        }
      }

      // ─ Layer 5 — Very faint noise dither (skipped on low-power) ─
      if (!lowPower) {
        const noiseAlpha = isDark ? 0.015 : 0.025;
        ctx.globalAlpha = noiseAlpha;
        ctx.fillStyle = isDark ? '#ffffff' : '#000000';
        for (let i = 0; i < 40; i++) {
          const x = Math.random() * width;
          const y = Math.random() * height;
          ctx.fillRect(x, y, 1, 1);
        }
        ctx.globalAlpha = 1;
      }
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(pulseTimer);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMouse as any);
    };
  }, [accent, neon, resolved]);

  return (
    <canvas
      ref={ref}
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        pointerEvents: 'none',
      }}
      data-testid="nexus-background"
    />
  );
};

export default NexusBackground;
